/**
 * usePlayerStreams — owns one PlayerConnection per media_server for the
 * player's current device set (a layout's unsaved draft, or a single camera)
 * and exposes the demuxed per-camera MediaStreams.
 *
 * The caller computes the desired per-device stream quality (the tile-size
 * rule lives in getQualityForTileWidth below) and the per-device overlay
 * subscription; this hook only reconciles connections to those wishes.
 *
 * Startup is gated on the panel entrance animation settling — N ICE
 * handshakes must not compete with the stage motion (camera_view precedent).
 */

import { useAtomValueRawSync, useSetAtom } from 'jotai';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { portalMonitor } from '@/api/portalMonitor';
import { isPanelTransitionOver, subscribePanelTransition } from '@/app/stageMotion';
import { useLatest } from '@/hooks/useLatest';
import { iceServersAtom } from '@/state/apiReads';
import { setPublishersAtom } from '@/state/ui/publishersAtoms';
import type {
    DeviceStreamInfo, FrameResolution, PlayerDevice, PlayerDiagnostics, PlayerState,
} from '@/types/player';
import { StreamQuality } from '@/types/player';

import { PlayerConnection } from './PlayerConnection';

/**
 * Tunable: a tile occupying at most this many PHYSICAL monitor pixels of
 * width (cssWidth x devicePixelRatio) streams the secondary (low) quality;
 * anything wider gets the primary stream. Physical pixels on purpose: they
 * are what the eye resolves, so a scaled display does not upgrade tiles the
 * monitor cannot show sharper anyway.
 */
export const kSecondaryMaxWidthPx = 640;

/** Quality monitor cadence and the consecutive-ticks stability gate. */
const kQualityMonitorMs = 500;
const kQualityStableTicks = 2;

/** The stream quality a tile of cssWidth CSS pixels deserves. */
export function getQualityForTileWidth(cssWidth: number): StreamQuality {
    const physicalPx = (cssWidth || 0) * (globalThis.devicePixelRatio || 1);
    return physicalPx > kSecondaryMaxWidthPx ? StreamQuality.High : StreamQuality.Low;
}

/**
 * Streams are keyed (serverId, deviceId), not deviceId alone: a session goes to one server, and
 * the parent server of a camera can change.
 */
export const streamKeyOf = (
    serverId: string, deviceId: string,
): string => `${serverId}|${deviceId}`;

/** Everything a host drives the player's sessions with. */
interface PlayerStreams {
    /** Keyed by streamKeyOf(serverId, deviceId). */
    streamsByKey: Map<string, MediaStream>;
    stateByServerId: Map<string, PlayerState>;
    getDeviceInfo: (serverId: string, deviceId: string) => Promise<DeviceStreamInfo | null>;
    /** Null when the device has no connection. */
    getDiagnostics: (serverId: string, deviceId: string) => PlayerDiagnostics | null;
    /** The stream a device is being sent; null when the device has no connection. */
    getStreamQuality: (serverId: string, deviceId: string) => StreamQuality | null;
    /** Null returns every session to live. */
    seekAll: (positionMs: number | null, isPrecise?: boolean) => void;
    /** Parks the position without sending a seek, for a caller whose command carries it. */
    parkAll: (positionMs: number) => void;
    setPaused: (isPaused: boolean, positionMs?: number | null) => void;
    /** Returns serverId to the command's seq, for matching the position reports. */
    stepFrame: (direction: number, positionMs?: number | null) => Map<string, number>;
    resolveFrame: (
        serverId: string, deviceId: string, rtpTimestamp: number, streamId?: string,
    ) => FrameResolution;
    reconnect: () => void;
    /** A tile has waited too long for its picture; see PlayerConnection.rebuildOnStall. */
    rebuildOnStall: (serverId: string, deviceId: string) => void;
    /** Turns talk-back on/off for a SET of cameras on their server's session, in one command -
   *  see PlayerConnection.setTalk. */
    setTalk: (serverId: string, deviceIds: readonly string[], isOn: boolean) => void;
    /** Sends one Opus packet for a camera's talk-back; false until the channel is open. */
    sendTalkAudio: (serverId: string, deviceId: string, opus: ArrayBuffer) => boolean;
}

/**
 * @param devices Unique devices of the player, in item order (index 0 of a
 *   server's group opens that server's session). Callers memoize.
 * @param metadataByDevice deviceId to whether its overlays are on.
 * @param isEnabled Master gate (panel visible, devices known).
 * @param getResumePositionMs Current archive playhead (epoch ms) or null when
 *   live; rebuilt and late-joining sessions resume there.
 * @param onCommandSent A session put a repositioning on the wire.
 */
export function usePlayerStreams(
    devices: PlayerDevice[],
    metadataByDevice: Record<string, boolean> | undefined,
    isEnabled: boolean,
    getResumePositionMs?: () => number | null,
    onStreamEof?: (serverId: string, deviceId: string) => void,
    onPosition?: (
        serverId: string, seq: number, positionsMsByDevice: Map<string, number>,
    ) => void,
    onCommandSent?: (serverId: string, positionMs: number | null, isPrecise: boolean) => void,
): PlayerStreams {
    const owner = useId();
    const setPublishers = useSetAtom(setPublishersAtom);
    const iceServers = useAtomValueRawSync(iceServersAtom);
    const iceServersRef = useLatest(iceServers);
    //< serverId to its PlayerConnection.
    const connectionsRef = useRef(new Map<string, PlayerConnection>());
    const getResumeRef = useLatest(getResumePositionMs);
    const onSentRef = useLatest(onCommandSent);
    const onEofRef = useLatest(onStreamEof);
    const onPositionRef = useLatest(onPosition);
    const [streamsByKey, setStreams] = useState(new Map<string, MediaStream>());
    const [stateByServerId, setStates] = useState(new Map<string, PlayerState>());
    // One-way latch: connections start only after the entrance animation
    // settles, and never re-park afterwards.
    const [isReleased, setReleased] = useState(() => isPanelTransitionOver());

    useEffect(() => {
        if (isReleased) { return undefined; }
        if (isPanelTransitionOver()) {
            setReleased(true);
            return undefined;
        }
        const unsub = subscribePanelTransition(() => {
            if (isPanelTransitionOver()) {
                setReleased(true);
            }
        });
        return () => {
            unsub();
        };
    }, [isReleased]);

    useEffect(() => {
        if (!isEnabled || !isReleased) { return; }
        const connections = connectionsRef.current;
        const byServer = new Map<string, PlayerDevice[]>();
        for (const device of devices) {
            if (!device.serverId || !device.deviceId) {
                continue;
            }
            if (!byServer.has(device.serverId)) {
                byServer.set(device.serverId, []);
            }
            byServer.get(device.serverId)?.push(device);
        }

        for (const [serverId, group] of byServer) {
            const existingPlayerConnection = connections.get(serverId);
            if (existingPlayerConnection) {
                existingPlayerConnection.setDevices(group);
            } else {
                connections.set(serverId, new PlayerConnection({
                    serverId,
                    devices: group,
                    onStreams: (serverStreams) => {
                        setStreams((prevAll) => {
                            // Replace this server's contribution wholesale.
                            const mergedMap = new Map<string, MediaStream>();
                            for (const [key, s] of prevAll) {
                                if (!key.startsWith(`${serverId}|`)) { mergedMap.set(key, s); }
                            }
                            for (const [id, s] of serverStreams) {
                                mergedMap.set(streamKeyOf(serverId, id), s);
                            }
                            return mergedMap;
                        });
                    },
                    onState: (state) => {
                        setStates((prevStates) => new Map(prevStates).set(serverId, state));
                    },
                    onPublishers: (ids, isOpen): void => setPublishers({ owner, ids,
                        isOpen }),
                    getIceServers: (): RTCIceServer[] => iceServersRef.current,
                    getResumePositionMs: () => getResumeRef.current?.() ?? null,
                    onCommandSent: (positionMs, isPrecise): void =>
                        onSentRef.current?.(serverId, positionMs, isPrecise),
                    onStreamEof: (deviceId) => onEofRef.current?.(serverId, deviceId),
                    onPosition: (seq, positionsByDevice) =>
                        onPositionRef.current?.(serverId, seq, positionsByDevice),
                }));
            }
        }
        // Servers no longer present in the device set: close their sessions.
        for (const [serverId, conn] of connections) {
            if (!byServer.has(serverId)) {
                conn.close();
                connections.delete(serverId);
                setStates((prevStates) => {
                    const nextMap = new Map(prevStates);
                    nextMap.delete(serverId);
                    return nextMap;
                });
            }
        }
        // Prune streams of devices that left the device set entirely.
        setStreams((prevStreams) => {
            const keep = new Set(devices.map((d) => streamKeyOf(d.serverId, d.deviceId)));
            if ([...prevStreams.keys()].every((key) => keep.has(key))) {
                return prevStreams;
            }
            const nextMap = new Map<string, MediaStream>();
            for (const [key, stream] of prevStreams) {
                if (keep.has(key)) {
                    nextMap.set(key, stream);
                }
            }
            return nextMap;
        });
    }, [
        devices, isEnabled, isReleased, owner, setPublishers, getResumeRef, iceServersRef,
        onEofRef,
        onPositionRef, onSentRef,
    ]);

    // Per-device overlay metadata subscription follows the toggles.
    // Also re-run when `devices` changes: a connection is created by the effect
    // above on a device-set change, and this reactive apply is the only path that
    // subscribes it to overlay metadata. Depending only on metadataByDevice missed
    // a connection that appeared after this last ran, so the channel opened with an
    // empty subscription and no metadata flowed until a toggle re-triggered it.
    useEffect(() => {
        if (!isEnabled || !isReleased) { return; }
        for (const conn of connectionsRef.current.values()) {
            for (const id of conn.getDeviceIds()) {
                conn.setDeviceMetadata(id, !!metadataByDevice?.[id]);
            }
        }
    }, [metadataByDevice, devices, isEnabled, isReleased]);

    // Periodic quality monitor: tile sizes flow into the desired per-device
    // qualities above; this tick delivers them with a stability gate — a
    // quality is committed only after it has held for TWO consecutive ticks,
    // so transient sizes mid-animation (expand, reflow, drag re-fit) never
    // flip the stream. The fast tick keeps the worst-case reaction around one
    // second, and re-delivery also heals commands lost to a still-opening data
    // channel or a rebuild.
    //< streamKey to the quality seen and how many consecutive ticks it has held.
    const stabilityRef = useRef(new Map<string, { quality: StreamQuality; ticks: number }>());
    useEffect(() => {
        if (!isEnabled || !isReleased) { return undefined; }
        const timer = setInterval(() => {
            const seen = new Set();
            for (const [serverId, conn] of connectionsRef.current) {
                for (const id of conn.getDeviceIds()) {
                    const key = streamKeyOf(serverId, id);
                    seen.add(key);
                    // A device always has a quality, so this fallback stands for nothing
                    // real — it only keeps the comparison below total. commitQuality
                    // re-reads the map and returns early when it finds no quality, so a
                    // value invented here can never reach the wire.
                    const desiredStreamQuality = conn.qualities.get(id) ?? StreamQuality.Low;
                    const entry = stabilityRef.current.get(key);
                    if (entry && entry.quality === desiredStreamQuality) {
                        entry.ticks += 1;
                        if (entry.ticks >= kQualityStableTicks) {
                            conn.commitQuality(id);
                        }
                    } else {
                        stabilityRef.current.set(key, { quality: desiredStreamQuality, ticks: 1 });
                    }
                }
            }
            for (const key of stabilityRef.current.keys()) {
                if (!seen.has(key)) {
                    stabilityRef.current.delete(key);
                }
            }
        }, kQualityMonitorMs);
        return () => clearInterval(timer);
    }, [isEnabled, isReleased]);

    // The retry plan waits up to 30 s; a tile has no reason to wait once the cloud is back.
    useEffect(() => portalMonitor.onRecovered((): void => {
        for (const conn of connectionsRef.current.values()) {
            conn.retryNow();
        }
    }), []);

    // Teardown on unmount only — the reconcile effect above owns live changes.
    useEffect(() => () => {
        for (const conn of connectionsRef.current.values()) {
            conn.close();
        }
        connectionsRef.current.clear();
    }, []);

    /** Codec/resolution/fps of a device's live track (see PlayerConnection). */
    const getDeviceInfo = useCallback((
        serverId: string, deviceId: string,
    ): Promise<DeviceStreamInfo | null> => {
        const conn = connectionsRef.current.get(serverId);
        return conn ? conn.getDeviceInfo(deviceId) : Promise.resolve(null);
    }, []);

    /** Seek/anchor snapshot of one device for the diagnostics HUD (see
   *  PlayerConnection.diagnostics); null when the device has no connection. */
    const getDiagnostics = useCallback((
        serverId: string, deviceId: string,
    ): PlayerDiagnostics | null => {
        const conn = connectionsRef.current.get(serverId);
        return conn ? conn.getDiagnostics(deviceId) : null;
    }, []);

    const getStreamQuality = useCallback((
        serverId: string, deviceId: string,
    ): StreamQuality | null => {
        const conn = connectionsRef.current.get(serverId);
        return conn ? conn.getDeliveredQuality(deviceId) : null;
    }, []);

    /**
   * Rebuilds every session from scratch (fresh WS, peer connection and
   * providers; archive position and pause re-applied on the new channel). The
   * recovery for a session that is CONNECTED but silent: the connection layer
   * heals a failed handshake or a dropped ICE pair, but a peer connection that
   * came up and carries no media looks healthy from here, so nothing rebuilds
   * it on its own.
   */
    const reconnect = useCallback((): void => {
        for (const conn of connectionsRef.current.values()) {
            conn.rebuild();
        }
    }, []);

    const rebuildOnStall = useCallback((serverId: string, deviceId: string): void => {
        connectionsRef.current.get(serverId)?.rebuildOnStall(deviceId);
    }, []);

    /** Seeks every session to positionMs (epoch ms); null returns to live. See
   *  PlayerConnection.seekAll for precise. */
    const seekAll = useCallback((positionMs: number | null, isPrecise = true): void => {
        for (const conn of connectionsRef.current.values()) {
            conn.seekAll(positionMs, isPrecise);
        }
    }, []);

    /** Parks positionMs as every session's archive position without sending a seek —
   *  for a caller about to change the device set, whose own command carries it. */
    const parkAll = useCallback((positionMs: number): void => {
        for (const conn of connectionsRef.current.values()) {
            conn.parkSeek(positionMs);
        }
    }, []);

    /** Classifies a presented frame of one device (see PlayerConnection.resolveFrame);
   *  a device without a connection stays hidden: its tile still holds the stream of the
   *  camera it replaced, from another server. */
    const resolveFrame = useCallback((
        serverId: string, deviceId: string, rtpTimestamp: number, streamId?: string,
    ): FrameResolution => {
        const conn = connectionsRef.current.get(serverId);
        return conn
            ? conn.resolveFrame(deviceId, rtpTimestamp, streamId)
            : { utcMs: 0, isPastGate: false };
    }, []);

    /** Pauses/resumes every provider of every session. positionMs, on resume, makes each
   *  server reposition there first (see PlayerConnection.setPaused). */
    const setPaused = useCallback((isPaused: boolean, positionMs: number | null = null): void => {
        for (const conn of connectionsRef.current.values()) {
            conn.setPaused(isPaused, positionMs);
        }
    }, []);

    /** Steps every paused session one video frame; direction < 0 steps back. Returns
   *  serverId → the command's seq, for matching the servers' position reports. */
    const stepFrame = useCallback((
        direction: number, positionMs: number | null = null,
    ): Map<string, number> => {
        const seqByServer = new Map<string, number>();
        for (const [serverId, conn] of connectionsRef.current) {
            const seq = conn.stepFrame(direction, positionMs);
            if (seq !== null) {
                seqByServer.set(serverId, seq);
            }
        }
        return seqByServer;
    }, []);

    /** Turns talk-back on/off for a set of cameras on their server's session, in one command. */
    const setTalk = useCallback((
        serverId: string, deviceIds: readonly string[], isOn: boolean,
    ): void => {
        connectionsRef.current.get(serverId)?.setTalk([...deviceIds], isOn);
    }, []);

    /** Sends one Opus packet for a camera's talk-back on its server's data channel. */
    const sendTalkAudio = useCallback((
        serverId: string, deviceId: string, opus: ArrayBuffer,
    ): boolean => {
        const conn = connectionsRef.current.get(serverId);
        return conn ? conn.sendTalkAudio(deviceId, opus) : false;
    }, []);

    return {
        streamsByKey, stateByServerId, getDeviceInfo, getDiagnostics, getStreamQuality, seekAll,
        parkAll, setPaused, stepFrame, resolveFrame, reconnect, rebuildOnStall, setTalk,
        sendTalkAudio,
    };
}
